import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Sun, Moon, Dumbbell, Coffee, Utensils, Sunset, Zap } from 'lucide-react';

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

// 0 = off, 1 = amber, 2 = orange (brand), 3 = coral
const DOSE_STYLES = [
  'bg-muted text-muted-foreground border-transparent hover:bg-muted/80',
  'bg-amber-500 text-white border-amber-500',
  'bg-primary text-white border-primary',
  'bg-[#EF4444] text-white border-[#EF4444]',
];

export const TIME_OPTIONS = [
  { value: 'morning',      label: 'Morning',      icon: Sun },
  { value: 'with_meals',   label: 'With Meals',   icon: Utensils },
  { value: 'pre_workout',  label: 'Pre-Workout',  icon: Zap },
  { value: 'post_workout', label: 'Post-Workout', icon: Dumbbell },
  { value: 'afternoon',    label: 'Afternoon',    icon: Coffee },
  { value: 'evening',      label: 'Evening',      icon: Sunset },
  { value: 'bedtime',      label: 'Bedtime',      icon: Moon },
];

export const DEFAULT_DAYS_OF_WEEK = { mon: 1, tue: 1, wed: 1, thu: 1, fri: 1, sat: 1, sun: 1 };

export function getFrequencyLabel(days, t) {
  const active = Object.values(days || {}).filter(v => v > 0).length;
  if (!t) {
    if (active === 7) return 'Every day';
    if (active === 2) return 'Twice a week';
    if (active === 1) return 'Once a week';
    if (active === 0) return 'Not scheduled';
    return `${active} days/week`;
  }
  if (active === 7) return t('supplements.freqEveryDay');
  if (active === 2) return t('supplements.freqTwiceWeek');
  if (active === 1) return t('supplements.freqOnceWeek');
  if (active === 0) return t('supplements.freqNotScheduled');
  return t('supplements.freqDaysPerWeek', { count: active });
}

/**
 * Day tile row — clicking a tile cycles doses: 0 → 1 → 2 → 3 → 0
 * Props:
 *   value: { mon: 0|1|2|3, tue: ..., wed: ..., thu: ..., fri: ..., sat: ..., sun: ... }
 *   onChange: (newValue) => void
 */
export function SupplementDayPicker({ value, onChange }) {
  const { t } = useTranslation();
  const days = value || DEFAULT_DAYS_OF_WEEK;

  const handleClick = (key) => {
    const current = days[key] ?? 0;
    const next = current >= 3 ? 0 : current + 1;
    onChange({ ...days, [key]: next });
  };

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1">
        {DAYS.map(({ key, label }) => {
          const dose = Math.min(Math.max(days[key] ?? 0, 0), 3);
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleClick(key)}
              className={`flex-1 flex flex-col items-center py-1.5 rounded-md border text-xs font-medium transition-colors ${DOSE_STYLES[dose]}`}
            >
              <span>{label}</span>
              {dose > 0 && <span className="text-xs font-bold leading-none mt-0.5">{dose}</span>}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">{getFrequencyLabel(days, t)}</p>
    </div>
  );
}

/**
 * Ordered times picker — renders N selects for N = maxDoses across all days.
 * Props:
 *   days: days_of_week dict
 *   times: string[] (ordered)
 *   onChange: (newTimes: string[]) => void
 */
export function OrderedTimesPicker({ days, times, onChange }) {
  const { t } = useTranslation();
  const maxDoses = Math.max(...Object.values(days || {}).map(v => Number(v) || 0), 0);
  if (maxDoses === 0) return null;

  // Ensure times array is the right length
  const current = Array.from({ length: maxDoses }, (_, i) => (times || [])[i] || '');

  const updateSlot = (i, val) => {
    const next = [...current];
    next[i] = val;
    onChange(next);
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{t('supplements.whenToTake')}</Label>
      {current.map((val, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-14 shrink-0">
            {maxDoses > 1 ? `Dose ${i + 1}` : 'Time'}
          </span>
          <Select value={val} onValueChange={v => updateSlot(i, v)}>
            <SelectTrigger className="h-11 md:h-8 text-xs flex-1">
              <SelectValue placeholder="Select time…" />
            </SelectTrigger>
            <SelectContent>
              {TIME_OPTIONS.map(opt => {
                const Icon = opt.icon;
                return (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="flex items-center gap-1.5">
                      <Icon className="h-3 w-3" />
                      {t(`supplements.time_${opt.value}`, opt.label)}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}
